import type { HelpPilotAnswer, PilotLine, TicketDetail } from '@epc/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { LifeBuoy, Sparkles, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { supportUi } from '../../lib/support';
import { Button } from '../../ui/button';
import { ErrorBox, toast } from '../../ui/feedback';
import { Field, Textarea } from '../../ui/form';

interface Turn {
  id: number;
  q: string;
  a: HelpPilotAnswer;
}

/**
 * Вопрос Pilot в панели «Помощь» (БЗ-01). Pilot отвечает по инструкции к этой странице и статьям базы знаний,
 * которые человек видит, и ссылается на них; ответ помечен «Подготовлено ИИ». «Помогло» закрывает вопрос без
 * человека (БЗ-23), «Написать в поддержку» отправляет обращение вместе с перепиской (БЗ-22).
 */
export function HelpPilot({ path, staff }: { path: string; staff: boolean }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  // С какого вопроса начинается ещё не отмеченная часть разговора: «Помогло» отправляет только её
  const [from, setFrom] = useState(0);
  const done = turns.length > 0 && from >= turns.length;

  // Ответ Pilot идёт с подписью сервера: без неё в обращении он записался бы как слова автора
  const transcript = (list: Turn[] = turns): PilotLine[] =>
    list.flatMap((t) => [
      { role: 'user' as const, text: t.q },
      { role: 'pilot' as const, text: t.a.text, sig: t.a.sig },
    ]);

  const ask = useMutation({
    mutationFn: (q: string) =>
      api<HelpPilotAnswer>('/support/help/ask', {
        method: 'POST',
        // Ответы «в инструкциях нет» модель не писала — в историю разговора они не идут
        body: { question: q, path, history: transcript(turns.filter((t) => t.a.answered)).slice(-10) },
      }),
    onSuccess: (a, q) => {
      setTurns((t) => [...t, { id: t.length, q, a }]);
      setQuestion('');
    },
  });

  const resolved = useMutation({
    mutationFn: () =>
      api<{ id: string; number: string }>('/support/help/resolved', {
        method: 'POST',
        body: { path, transcript: transcript(turns.slice(from)).slice(-40) },
      }),
    onSuccess: () => {
      setFrom(turns.length);
      toast('Спасибо! Отметили, что Pilot помог');
      qc.invalidateQueries({ queryKey: ['support'] });
    },
  });

  const escalate = useMutation({
    mutationFn: () => {
      const first = turns[0]!.q;
      return api<TicketDetail>('/support/tickets', {
        method: 'POST',
        body: {
          subject: first.length > 200 ? `${first.slice(0, 197)}...` : first.padEnd(3, '.'),
          kind: 'question',
          body: turns
            .map((t) => t.q)
            .join('\n\n')
            .slice(0, 4000),
          pagePath: path,
          pageTitle: document.title.slice(0, 200),
          pilotTranscript: transcript().slice(-40),
        },
      });
    },
    onSuccess: (t) => {
      toast(`Обращение ${t.number} отправлено`);
      qc.invalidateQueries({ queryKey: ['support'] });
      supportUi.closeHelp();
      nav({ to: '/support/tickets/$id', params: { id: t.id } });
    },
  });

  const last = turns[turns.length - 1];
  const error = ask.error ?? resolved.error ?? escalate.error;
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-line bg-surface-2 p-4">
      <h4 className="flex items-center gap-2 text-[14px] font-semibold">
        <Sparkles className="size-4" aria-hidden /> Спросить Pilot
      </h4>
      {turns.map((t) => (
        <div key={t.id} className="flex flex-col gap-1.5">
          <p className="self-end rounded-md bg-tile px-3 py-2 leading-6">{t.q}</p>
          <div className="rounded-md border border-line bg-surface px-3 py-2">
            <p className="whitespace-pre-line leading-6">{t.a.text}</p>
            {t.a.sources.length ? (
              <ul className="mt-2 flex flex-col gap-1">
                {t.a.sources.map((s) => (
                  <li key={s.path + s.title}>
                    <Link
                      to={s.path}
                      onClick={() => supportUi.closeHelp()}
                      className="inline-flex min-h-11 items-center text-[13px] text-accent underline md:min-h-0"
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="mt-1 text-[12px] text-ink-2">{t.a.label}</p>
          </div>
        </div>
      ))}
      <Field label={turns.length ? 'Уточнить' : 'Ваш вопрос о работе в портале'} htmlFor="help-pilot-q">
        <Textarea
          id="help-pilot-q"
          rows={2}
          maxLength={1000}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Например: как отправить отчёт смены на проверку?"
        />
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          loading={ask.isPending}
          disabled={question.trim().length < 3}
          onClick={() => ask.mutate(question.trim())}
        >
          Спросить
        </Button>
        {last && staff && !done ? (
          <>
            {last.a.answered ? (
              <Button
                icon={<ThumbsUp className="size-4" />}
                loading={resolved.isPending}
                onClick={() => resolved.mutate()}
              >
                Помогло
              </Button>
            ) : null}
            <Button
              icon={<LifeBuoy className="size-4" />}
              loading={escalate.isPending}
              onClick={() => escalate.mutate()}
            >
              {last.a.answered ? 'Не помогло — в поддержку' : 'Написать в поддержку'}
            </Button>
          </>
        ) : null}
      </div>
      {error ? (
        <ErrorBox error={error instanceof ApiError ? error : new Error('Не удалось получить ответ')} />
      ) : null}
      <p className="text-[12px] text-ink-2">
        Pilot отвечает по инструкциям портала и видит только то, что открыто вам. Ответ готовит ИИ —
        проверяйте важное.
      </p>
    </section>
  );
}
